import HCaptcha from '@hcaptcha/react-hcaptcha';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useT } from '@kmgestao/i18n';
import { useTheme } from '@kmgestao/ui';

/** Test key oficial do hCaptcha — renderiza com um aviso sobreposto que
 * quebra a UI. Em dev, detectamos e pulamos o widget. */
const HCAPTCHA_TEST_KEY = '10000000-ffff-ffff-ffff-000000000001';
const configuredSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY ?? '';
/** Em dev sem site key real (vazio ou test-key), pulamos o widget e emitimos
 * um token-placeholder. Supabase local também não valida o token por padrão. */
const shouldSkipCaptcha =
  import.meta.env.DEV && (!configuredSiteKey || configuredSiteKey === HCAPTCHA_TEST_KEY);

export interface CaptchaFieldHandle {
  /** Reseta o widget (usar após erro de login para forçar novo desafio). */
  reset: () => void;
}

export interface CaptchaFieldProps {
  /** Dispara quando o usuário resolve o captcha, com o token de verificação. */
  onVerify: (token: string) => void;
  /** Dispara quando o token expira (tipicamente após 2 min). */
  onExpire?: () => void;
  /** Mensagem de erro de validação (exibe abaixo do widget). */
  error?: string | undefined;
}

/**
 * Captcha hCaptcha com ref externo para reset. Segue o tema automaticamente
 * via `useTheme`.
 *
 * Em desenvolvimento, se `VITE_HCAPTCHA_SITE_KEY` não estiver configurado,
 * o widget é **omitido** e um token-placeholder é emitido. Assim o fluxo
 * de auth funciona end-to-end em local sem o aviso sobreposto da test-key.
 */
export const CaptchaField = forwardRef<CaptchaFieldHandle, CaptchaFieldProps>(function CaptchaField(
  { onVerify, onExpire, error },
  ref,
) {
  const { resolvedTheme } = useTheme();
  const { t } = useT('auth');
  const innerRef = useRef<HCaptcha>(null);

  useImperativeHandle(ref, () => ({
    reset: () => innerRef.current?.resetCaptcha(),
  }));

  // Auto-emite token dev na primeira render — sem depender do usuário clicar
  useEffect(() => {
    if (shouldSkipCaptcha) onVerify('dev-captcha-bypass');
    // biome-ignore/eslint: intencionalmente só no mount; `onVerify` é estável por design
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shouldSkipCaptcha) {
    return (
      <div
        role="note"
        className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
      >
        {t('captcha.devDisabledPrefix')}{' '}
        <code className="font-mono text-xs">{t('captcha.devEnvVar')}</code>{' '}
        {t('captcha.devDisabledSuffix')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <HCaptcha
        ref={innerRef}
        sitekey={configuredSiteKey}
        theme={resolvedTheme}
        onVerify={onVerify}
        onExpire={onExpire ?? (() => undefined)}
      />
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
